/**
 * @fileoverview Base API client with interceptors and error handling
 * Provides a centralized HTTP client for all API operations
 */

import { API_CONFIG, HTTP_METHODS, type HttpMethod, type RequestConfig } from './endpoints';
import { ApiError, createErrorFromResponse, createErrorFromNetworkError } from './errors';

// Request interceptor type
export type RequestInterceptor = (config: RequestConfig & { url: string; method: HttpMethod; body?: unknown }) => RequestConfig & { url: string; method: HttpMethod; body?: unknown } | Promise<RequestConfig & { url: string; method: HttpMethod; body?: unknown }>;

// Response interceptor type
export type ResponseInterceptor = (response: Response, data: unknown) => unknown | Promise<unknown>;

// Error interceptor type
export type ErrorInterceptor = (error: ApiError) => ApiError | Promise<ApiError>;

class ApiClient {
  private baseURL: string;
  private defaultTimeout: number;
  private requestInterceptors: RequestInterceptor[] = [];
  private responseInterceptors: ResponseInterceptor[] = [];
  private errorInterceptors: ErrorInterceptor[] = [];

  constructor(baseURL: string = API_CONFIG.BASE_URL, timeout: number = API_CONFIG.TIMEOUT) {
    this.baseURL = baseURL;
    this.defaultTimeout = timeout;
    
    // Add default request interceptor for auth token
    this.addRequestInterceptor(this.authInterceptor);
    
    // Add default error interceptor for handling 401 Unauthorized
    this.addErrorInterceptor(this.unauthorizedErrorInterceptor);
    
    // Add error interceptor for 402 Insufficient Credits
    this.addErrorInterceptor(this.insufficientCreditsInterceptor);
    
    // Add default response interceptor for logging
    if (import.meta.env.DEV) {
      this.addResponseInterceptor(this.loggingInterceptor);
    }
  }

  // Add request interceptor
  addRequestInterceptor(interceptor: RequestInterceptor): void {
    this.requestInterceptors.push(interceptor);
  }

  // Add response interceptor
  addResponseInterceptor(interceptor: ResponseInterceptor): void {
    this.responseInterceptors.push(interceptor);
  }

  // Add error interceptor
  addErrorInterceptor(interceptor: ErrorInterceptor): void {
    this.errorInterceptors.push(interceptor);
  }

    // Default auth interceptor
  private authInterceptor: RequestInterceptor = (config) => {
    const token = this.getAuthToken();
    
    // Set default headers if not already present
    if (!config.headers) {
      config.headers = {};
    }
    if (token) {
      (config.headers as Record<string, string>)['Authorization'] = `Bearer ${token}`;
    }
    return config;
  };

  // Default logging interceptor
  private loggingInterceptor: ResponseInterceptor = (_response, data) => {
    // Logging disabled to prevent React DevTools crashes
    return data;
  };

  // Default error interceptor for handling 401 Unauthorized
  // Note: token refresh + retry is handled directly in the request() method.
  // This interceptor only handles the fallback redirect when refresh is not possible.
  private unauthorizedErrorInterceptor: ErrorInterceptor = async (error) => {
    if (error.code === 'UNAUTHORIZED' && error.details?.status === 401) {
      localStorage.removeItem('authToken');
      localStorage.removeItem('refreshToken');

      const publicPaths = ['/login', '/signup', '/', '/pricing'];
      const currentPath = window.location.pathname;

      if (!publicPaths.includes(currentPath)) {
        window.location.href = '/login';
      }
    }

    return error;
  };

  // Error interceptor for 402 Insufficient Credits
  private insufficientCreditsInterceptor: ErrorInterceptor = (error) => {
    if (error.code === 'INSUFFICIENT_BALANCE' && error.details) {
      const { creditBalance, required } = error.details as { creditBalance?: number; required?: number };
      if (creditBalance !== undefined && required !== undefined) {
        error = new ApiError(
          error.code,
          `Insufficient credits: you have ${creditBalance} but need ${required}. Visit Billing to manage your plan.`,
          error.details
        );
      }
    }
    return error;
  };

  // Get auth token from storage
  private getAuthToken(): string | null {
    return localStorage.getItem('authToken');
  }

  // Build full URL
  private buildURL(endpoint: string): string {
    if (endpoint.startsWith('http')) {
      return endpoint;
    }
    return `${this.baseURL}${endpoint.startsWith('/') ? endpoint : `/${endpoint}`}`;
  }

  // Apply request interceptors
  private async applyRequestInterceptors(config: RequestConfig & { url: string; method: HttpMethod; body?: unknown }) {
    let modifiedConfig = config;
    for (const interceptor of this.requestInterceptors) {
      modifiedConfig = await interceptor(modifiedConfig);
    }
    return modifiedConfig;
  }

  // Apply response interceptors
  private async applyResponseInterceptors(response: Response, data: unknown) {
    let modifiedData = data;
    for (const interceptor of this.responseInterceptors) {
      modifiedData = await interceptor(response, modifiedData);
    }
    return modifiedData;
  }

  // Apply error interceptors
  private async applyErrorInterceptors(error: ApiError): Promise<ApiError> {
    let modifiedError = error;
    for (const interceptor of this.errorInterceptors) {
      modifiedError = await interceptor(modifiedError);
    }
    return modifiedError;
  }

  // Retry logic
  private async withRetry<T>(
    operation: () => Promise<T>,
    attempts: number = API_CONFIG.RETRY_ATTEMPTS,
    delay: number = API_CONFIG.RETRY_DELAY
  ): Promise<T> {
    try {
      return await operation();
    } catch (error) {
      if (attempts > 1 && this.isRetryableError(error)) {
        await this.sleep(delay);
        return this.withRetry(operation, attempts - 1, delay * 2); // Exponential backoff
      }
      throw error;
    }
  }

  // Check if error is retryable
  private isRetryableError(error: unknown): boolean {
    if (error instanceof ApiError) {
      return error.isRetryable();
    }
    // Fallback for other errors
    if (error instanceof Error) {
      return error.message.includes('Failed to fetch') || 
             error.message.includes('NetworkError') ||
             error.message.includes('HTTP 5');
    }
    return false;
  }

  // Sleep utility
  private sleep(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  // Main request method
  async request<T = unknown>(
    endpoint: string,
    method: HttpMethod = HTTP_METHODS.GET,
    body?: unknown,
    config: RequestConfig = {}
  ): Promise<T> {
    const url = this.buildURL(endpoint);
    const timeout = config.timeout || this.defaultTimeout;
    const shouldRetry = config.retry !== false;

    // Prepare request configuration
    const requestConfig = await this.applyRequestInterceptors({
      url,
      method,
      body,
      ...config,
      headers: {
        'Content-Type': 'application/json',
        ...config.headers,
      },
    });

    const executeRequest = async (): Promise<T> => {
      // Create abort controller for timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), timeout);

      try {
        // Prepare fetch options
        const fetchOptions: RequestInit = {
          method: requestConfig.method,
          headers: requestConfig.headers,
          signal: controller.signal,
          credentials: requestConfig.credentials ?? 'include',
        };

        // Add body if present
        if (requestConfig.body !== undefined) {
          fetchOptions.body = typeof requestConfig.body === 'string' 
            ? requestConfig.body 
            : JSON.stringify(requestConfig.body);
        }

        // Add other options (excluding our custom properties)
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { timeout: _timeout, retry: _retry, retryAttempts: _retryAttempts, retryDelay: _retryDelay, body: _body, credentials: _credentials, ...otherOptions } = requestConfig;
        Object.assign(fetchOptions, otherOptions);

        // Make the request
        const response = await fetch(requestConfig.url, fetchOptions);
        clearTimeout(timeoutId);

        // Parse response
        let data: unknown;
        const contentType = response.headers.get('content-type');
        
        if (contentType && contentType.includes('application/json')) {
          data = await response.json();
        } else {
          data = await response.text();
        }

        // Check if request was successful
        if (!response.ok) {
          // On 401: attempt a silent token refresh and retry the request once
          if (response.status === 401 && !config._isRetry) {
            const refreshToken = localStorage.getItem('refreshToken');
            if (refreshToken) {
              try {
                const refreshRes = await fetch(`${this.baseURL}/auth/refresh-token`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ refreshToken }),
                });
                if (refreshRes.ok) {
                  const refreshData = await refreshRes.json();
                  if (refreshData.token) {
                    localStorage.setItem('authToken', refreshData.token);
                    // Retry the original request with the new token
                    return this.request<T>(endpoint, method, body, { ...config, _isRetry: true });
                  }
                }
              } catch {
                // Refresh request itself failed — fall through to error handling
              }
            }
          }

          const error = await createErrorFromResponse(
            response, 
            requestConfig.url, 
            requestConfig.method,
            data // Pass the already-parsed data
          );
          throw await this.applyErrorInterceptors(error);
        }

        // Apply response interceptors
        const processedData = await this.applyResponseInterceptors(response, data);
        return processedData as T;

      } catch (error) {
        clearTimeout(timeoutId);
        
        // Handle network and other errors using our utility
        if (error instanceof ApiError) {
          // Re-throw API errors (already processed)
          throw error;
        }
        
        // Convert other errors to ApiError
        const errorToConvert = error instanceof Error ? error : new Error(String(error));
        const apiError = createErrorFromNetworkError(errorToConvert);
        throw await this.applyErrorInterceptors(apiError);
      }
    };

    // Execute with or without retry
    if (shouldRetry) {
      return this.withRetry(executeRequest, config.retryAttempts, config.retryDelay);
    } else {
      return executeRequest();
    }
  }

  // Convenience methods
  async get<T = unknown>(endpoint: string, config?: RequestConfig): Promise<T> {
    return this.request<T>(endpoint, HTTP_METHODS.GET, undefined, config);
  }

  async post<T = unknown>(endpoint: string, body?: unknown, config?: RequestConfig): Promise<T> {
    return this.request<T>(endpoint, HTTP_METHODS.POST, body, config);
  }

  async put<T = unknown>(endpoint: string, body?: unknown, config?: RequestConfig): Promise<T> {
    return this.request<T>(endpoint, HTTP_METHODS.PUT, body, config);
  }

  async patch<T = unknown>(endpoint: string, body?: unknown, config?: RequestConfig): Promise<T> {
    return this.request<T>(endpoint, HTTP_METHODS.PATCH, body, config);
  }

  async delete<T = unknown>(endpoint: string, config?: RequestConfig): Promise<T> {
    return this.request<T>(endpoint, HTTP_METHODS.DELETE, undefined, config);
  }
}

// Create singleton instance
export const apiClient = new ApiClient();

// Export main class
export { ApiClient };
